import {exportChartImage} from "./utilities";
import {buildChartOptions, fetchMap, renderChart} from "./chartRenderer";

/**
 * The one component every Highcharts chart is drawn with. What sets a chart
 * type apart is decided on the server, which hands over the options, the
 * series and any extras to draw on top (see App\Domain\Charting\Presets).
 */
export default function highchartsChart() {
    return {
        chart: null,

        init() {
            setTimeout(() => {
                this.draw(this.$wire);
            }, 0);
        },

        chartContainer: {
            ['@export-chart.window'](event) {
                exportChartImage(this.chart, event.detail);
            },
            ['@toggle-series.window'](event) {
                const series = this.chart.series.find((series) => {
                    return series.options.id == event.detail.seriesId;
                });

                if (!series) {
                    return;
                }

                if (event.detail.visible) {
                    series.show();
                } else {
                    series.hide();
                }
            },
            ['@highlight-series.window'](event) {
                const seriesId = event.detail.seriesId;

                if (!seriesId) {
                    console.error('Series ID is required to highlight a series');
                    return;
                }

                const isHighlighted = event.detail.highlighted || false;
                const higlightedSeriesState = isHighlighted ? 'hover' : 'normal';
                const otherSeriesState = isHighlighted ? 'inactive' : 'normal';

                this.chart.series.forEach((series) => {
                    if (series.options.id == seriesId) {
                        series.setState(higlightedSeriesState, true);
                    } else {
                        series.setState(otherSeriesState, true);
                    }
                });
            },
        },

        async draw(component) {
            const chartContainer = this.$refs.container;

            const data = component.get('data') || {};
            const customOptions = component.get('options') || {};
            const extras = component.get('extras') || {};

            const combinedOptions = buildChartOptions({
                series: data,
                customOptions,
                extras,
                container: chartContainer,
                title: component.get('title') || '',
            });

            if (extras.map) {
                combinedOptions.chart.map = await fetchMap(extras.map);
            }

            if (extras.supportsPowergridBreakdown) {
                combinedOptions.plotOptions = combinedOptions.plotOptions || {};
                combinedOptions.plotOptions.series = combinedOptions.plotOptions.series || {};
                combinedOptions.plotOptions.series.cursor = 'pointer';
                combinedOptions.plotOptions.series.point = {
                    events: {
                        click: function () {
                            window.Livewire.dispatch('openModal', {
                                component: 'modals.powergrid-breakdown', arguments: {
                                    filters: this.options.breakdownFilters,
                                }
                            });
                        }
                    }
                };
            }

            if (extras.supportsGlobalBreakdown) {
                combinedOptions.plotOptions = combinedOptions.plotOptions || {};
                combinedOptions.plotOptions.series = combinedOptions.plotOptions.series || {};
                combinedOptions.plotOptions.series.cursor = 'pointer';
                combinedOptions.plotOptions.series.point = {
                    events: {
                        click: function () {
                            window.Livewire.dispatch('openModal', {
                                component: 'modals.global-breakdown', arguments: {
                                    filters: this.options.globalBreakdownFilters,
                                    modelClass: this.options.modelClass,
                                }
                            });
                        }
                    }
                };
            }

            // Points carrying a `slideOver` payload ({component, arguments})
            // open it when clicked, e.g. the comments behind a topic.
            if (extras.pointSlideOvers) {
                combinedOptions.plotOptions = combinedOptions.plotOptions || {};
                combinedOptions.plotOptions.series = combinedOptions.plotOptions.series || {};
                combinedOptions.plotOptions.series.cursor = 'pointer';
                combinedOptions.plotOptions.series.point = {
                    events: {
                        click: function () {
                            if (this.options.slideOver) {
                                window.Livewire.dispatch('slide-over.open', this.options.slideOver);
                            }
                        }
                    }
                };
            }

            this.chart = renderChart({
                Highcharts: window.Highcharts,
                container: chartContainer,
                options: combinedOptions,
                existingChart: this.chart,
                chartConstructor: extras.chartConstructor || 'chart',
            });
        }
    };
};
